import { toast as baseToast } from 'react-toastify';

/**
 * Drop-in replacement for react-toastify's `toast` that collapses duplicate
 * messages. If the same message is already on screen, instead of stacking a
 * new toast we update the existing one with a repeat counter (×N) and restart
 * its auto-close timer. Prevents walls of identical "server unreachable"
 * toasts when a request is retried or fails in a loop.
 */

const repeatCounts = new Map();

const renderWithCount = (content, count) => (
  <span className="toast-dedupe">
    {content}
    <span className="toast-repeat-count" title={`Repeated ${count} times`}>
      ×{count}
    </span>
  </span>
);

const withDedupe = (fire, typeKey) => (content, options = {}) => {
  // Only string messages can be safely compared; JSX content passes through.
  if (typeof content !== 'string') {
    return fire(content, options);
  }

  const id = options.toastId ?? `${typeKey}:${content}`;

  if (baseToast.isActive(id)) {
    const count = (repeatCounts.get(id) || 1) + 1;
    repeatCounts.set(id, count);
    baseToast.update(id, {
      ...options,
      render: renderWithCount(content, count),
      // restart the dismiss timer so the toast stays up while errors repeat
      autoClose: options.autoClose ?? 5000,
    });
    return id;
  }

  repeatCounts.set(id, 1);
  return fire(content, {
    ...options,
    toastId: id,
    onClose: (...args) => {
      repeatCounts.delete(id);
      options.onClose?.(...args);
    },
  });
};

const toast = withDedupe(baseToast, 'default');

// Carry over everything else (dismiss, update, isActive, promise, loading,
// POSITION/TYPE constants, ...) so this stays a drop-in replacement.
Object.assign(toast, baseToast);

toast.success = withDedupe(baseToast.success, 'success');
toast.error = withDedupe(baseToast.error, 'error');
toast.info = withDedupe(baseToast.info, 'info');
toast.warning = withDedupe(baseToast.warning, 'warning');
toast.warn = toast.warning;

export { toast };
export default toast;
